// Z86: per-warning tick boxes. Each warning must be ticked individually (no "accept all"); only the
// duplicate-invoice-number warning (red) forces a reason, the rest take an optional one.
import { WARNING_LABEL, type AckState, type Warning } from '../../lib/supplierInvoices'

interface Props {
  warnings: Warning[]
  state: AckState
  onChange: (next: AckState) => void
  // Optional label per cost line, so a warning can say which line it is about.
  lineLabel?: (chargeId: string) => string | undefined
}

function DetailList({ detail }: { detail: unknown }) {
  if (!Array.isArray(detail) || detail.length === 0) return null
  return (
    <ul className="mt-1 space-y-0.5 text-xs">
      {detail.slice(0, 8).map((d: Record<string, unknown>, i) => (
        <li key={i} className="flex flex-wrap gap-x-2">
          {d.invoice_id ? (
            <a className="underline" target="_blank" rel="noreferrer" href={`/accounts?tab=cost&supplier_invoice=${d.invoice_id}`}>
              Invoice {String(d.invoice_no ?? '')}
            </a>
          ) : d.id ? (
            <a className="underline" target="_blank" rel="noreferrer" href={`/accounts?tab=cost&supplier_invoice=${d.id}`}>
              Invoice {String(d.invoice_no ?? '')}
            </a>
          ) : null}
          {d.invoice_date ? <span>{String(d.invoice_date)}</span> : null}
          {d.amount != null ? <span>{String(d.amount)} {String(d.currency ?? '')}</span> : null}
          {d.net_amount != null ? <span>net {String(d.net_amount)} {String(d.currency ?? '')}</span> : null}
          {d.status ? <span>{String(d.status)}</span> : null}
        </li>
      ))}
    </ul>
  )
}

export default function WarningAckList({ warnings, state, onChange, lineLabel }: Props) {
  if (warnings.length === 0) return null
  const set = (key: string, patch: Partial<{ checked: boolean; reason: string }>) =>
    onChange({ ...state, [key]: { ...(state[key] ?? { checked: false, reason: '' }), ...patch } })
  return (
    <div className="space-y-2">
      {warnings.map(w => {
        const strong = w.severity === 'strong'
        const a = state[w.key] ?? { checked: false, reason: '' }
        const line = w.job_charge_id ? lineLabel?.(w.job_charge_id) : undefined
        return (
          <div key={w.key} className={`rounded-lg border p-3 text-sm ${strong
            ? 'border-red-400 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
            : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200'}`}>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={a.checked} onChange={e => set(w.key, { checked: e.target.checked })} />
              <span>
                <span className="font-semibold">{WARNING_LABEL[w.type] ?? w.type}</span>
                {line && <span className="ml-2 text-xs opacity-80">({line})</span>}
                <span className="block">{w.message}</span>
                <DetailList detail={w.detail} />
                <span className="mt-1 block text-xs font-medium">
                  {strong ? 'Tick to confirm this is a different invoice and give a reason.' : 'Tick to confirm you have checked this.'}
                </span>
              </span>
            </label>
            <textarea
              value={a.reason}
              onChange={e => set(w.key, { reason: e.target.value })}
              rows={1}
              placeholder={w.requires_reason ? 'Reason (required)' : 'Reason (optional)'}
              className={`mt-2 w-full rounded border bg-white px-2 py-1 text-sm text-gray-900 dark:bg-gray-800 dark:text-white ${
                w.requires_reason && a.checked && a.reason.trim() === '' ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
            />
          </div>
        )
      })}
    </div>
  )
}
